import { useEffect, useState } from "react";

import {
  fetchPublicSeoSerpSettings,
  type PublicSeoSerpSettings,
  type PublicSeoSerpTemplate,
} from "./seo";

import { useSsrRouteData } from "@/ssr/ssrContext";

let cachedSettings: PublicSeoSerpSettings | null | undefined;
let settingsPromise: Promise<{ settings: PublicSeoSerpSettings }> | null = null;

// Les surcharges de l'admin voyagent dans le HTML rendu côté serveur : sans
// elles, le premier rendu (celui que lit un crawler) retomberait sur les
// gabarits par défaut, et ne se corrigerait qu'après le fetch. Elles servent
// aussi de repli si cet appel échoue.
const resolveSettings = (
  ssrTemplates?: Record<string, PublicSeoSerpTemplate>,
) => cachedSettings ?? (ssrTemplates ? { templates: ssrTemplates } : null);

export function useSeoSerpSettings() {
  const ssrTemplates = useSsrRouteData().seoSerpTemplates;
  const [settings, setSettings] = useState<PublicSeoSerpSettings | null>(
    resolveSettings(ssrTemplates),
  );

  useEffect(() => {
    let isMounted = true;

    if (cachedSettings !== undefined) {
      setSettings(resolveSettings(ssrTemplates));

      return () => {
        isMounted = false;
      };
    }
    if (!settingsPromise) {
      settingsPromise = fetchPublicSeoSerpSettings();
    }

    settingsPromise
      .then((res) => {
        if (!isMounted) return;
        cachedSettings = res.settings;
        setSettings(cachedSettings);
      })
      .catch(() => {
        if (!isMounted) return;
        cachedSettings = null;
        setSettings(resolveSettings(ssrTemplates));
      })
      .finally(() => {
        settingsPromise = null;
      });

    return () => {
      isMounted = false;
    };
  }, [ssrTemplates]);

  return settings;
}
